import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Badge  from "@react/shared/ui/Badge"

const COLUMN_ROLES = ["code", "description", "unit", "quantity", "unit_price", "total", "details"]

// A DPGF index: 1, 1.2, 2.1.3.4, 01.02.003, A.1, LOT 2, 6.4.2.a.
const INDEX_PATTERN = /^(lot\s*)?([a-z]{1,2}[.\-]?)?\d{1,3}([.\-]\d{1,3})*([.\-]?[a-z])?[.)]?$/i

// Plain counts (1, 2, 140) match too: an index also shows depth or a LOT/letter prefix.
const looksLikeIndex = values => {
  const filled  = values.map(value => String(value || "").trim()).filter(Boolean)
  const indexes = filled.filter(value => INDEX_PATTERN.test(value))
  if (filled.length < 2 || indexes.length / filled.length < 0.6) return false

  const depths = new Set(indexes.map(value => value.replace(/[.)]$/, "").split(/[.\-]/).length))
  const deepest = Math.max(...depths)
  return indexes.some(value => /^(lot|[a-z])/i.test(value)) || deepest >= 3 || (depths.size > 1 && deepest > 1)
}

const ROLE_TINT = {
  description: { cell: "bg-indigo-50/70",  head: "bg-indigo-100 text-indigo-800 border-indigo-200",   dot: "bg-indigo-500" },
  code:        { cell: "bg-purple-50/70",  head: "bg-purple-100 text-purple-800 border-purple-200",   dot: "bg-purple-500" },
  unit:        { cell: "bg-amber-50/70",   head: "bg-amber-100 text-amber-800 border-amber-200",      dot: "bg-amber-500" },
  quantity:    { cell: "bg-emerald-50/70", head: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  unit_price:  { cell: "bg-sky-50/70",     head: "bg-sky-100 text-sky-800 border-sky-200",            dot: "bg-sky-500" },
  total:       { cell: "bg-rose-50/70",    head: "bg-rose-100 text-rose-800 border-rose-200",         dot: "bg-rose-500" },
  details:     { cell: "bg-teal-50/70",    head: "bg-teal-100 text-teal-800 border-teal-200",         dot: "bg-teal-500" },
}

const HIERARCHY_OPTIONS = [
  { value: "auto",                  icon: "wand-magic-sparkles" },
  { value: "code",                  icon: "list-ol" },
  { value: "description_numbering", icon: "arrow-down-1-9" },
  { value: "indentation",           icon: "indent" },
  { value: "headings",              icon: "heading" },
]

const SPECS_OPTIONS = [
  { value: "long_description", icon: "align-left" },
  { value: "lines",            icon: "list" },
  { value: "ignore",           icon: "eye-slash" },
]

const PRICES_OPTIONS = [
  { value: "none",             icon: "ban" },
  { value: "cost_equals_sale", icon: "equals" },
  { value: "cost_zero",        icon: "pen" },
]

const STEP_ICONS = {
  columns:   "table-columns",
  hierarchy: "sitemap",
  specs:     "tags",
  questions: "circle-question",
  prices:    "euro-sign",
  summary:   "list-check",
}

const t = (key, options) => I18n.t(`quotes.project_index.configure_import.${key}`, options)

const Stepper = ({ steps, current, reached, onJump }) => (
  <ol className="flex items-center">
    {steps.map((key, index) => {
      const done      = index < current
      const active    = index === current
      const reachable = index <= reached
      return (
        <li key={key} className={`flex items-center ${index < steps.length - 1 ? "flex-1" : ""}`}>
          <button
            type      = "button"
            disabled  = {!reachable}
            onClick   = {() => onJump(index)}
            className = "group flex flex-col items-center gap-1.5 disabled:cursor-not-allowed"
          >
            <span className={`inline-grid place-items-center w-9 h-9 rounded-full text-sm transition-all duration-300 ${
              active
                ? "bg-brand-500 text-white ring-4 ring-brand-100 shadow-sm"
                : done
                  ? "bg-brand-500 text-white group-hover:bg-brand-600"
                  : "bg-white text-slate-400 border border-slate-200"
            }`}>
              <FontAwesomeIcon icon={done ? "check" : STEP_ICONS[key]} />
            </span>
            <span className={`text-[11px] font-medium whitespace-nowrap ${active ? "text-slate-900" : done ? "text-brand-700" : "text-slate-400"}`}>
              {t(`steps.${key}`)}
            </span>
          </button>
          {index < steps.length - 1 && (
            <span className="relative flex-1 h-0.5 mx-2 mb-5 rounded-full bg-slate-200 overflow-hidden">
              <span className={`absolute inset-y-0 left-0 bg-brand-500 transition-all duration-500 ${done ? "w-full" : "w-0"}`} />
            </span>
          )}
        </li>
      )
    })}
  </ol>
)

const StepHeader = ({ title, hint }) => (
  <div>
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
  </div>
)

const Advice = ({ reason }) => {
  if (!reason) return null
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-brand-50 to-white border border-brand-100">
      <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-brand-500 text-white shrink-0">
        <FontAwesomeIcon icon="wand-magic-sparkles" className="text-xs" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">{t("ai_advice")}</div>
        <p className="text-sm text-slate-700 mt-0.5">{reason}</p>
      </div>
    </div>
  )
}

const MarkBadge = ({ kind }) => (
  <Badge tone="brand" variant="soft" size="xs">
    <FontAwesomeIcon icon={kind === "detected" ? "wand-magic-sparkles" : "star"} className="mr-1" />
    {t(kind === "detected" ? "detected_badge" : "recommended_badge")}
  </Badge>
)

const OptionCards = ({ name, options, value, onChange, marked, markKind = "recommended", disabledWhen, columns = 3 }) => (
  <div role="radiogroup" className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${columns === 3 ? "lg:grid-cols-3" : ""}`}>
    {options.map(option => {
      const checked  = value === option.value
      const disabled = disabledWhen?.(option.value)
      return (
        <label
          key       = {option.value}
          className = {`relative flex flex-col gap-2 p-4 rounded-xl border transition-all duration-200 ${
            disabled
              ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
              : checked
                ? "border-brand-500 bg-brand-50/70 ring-1 ring-brand-500 shadow-sm cursor-pointer"
                : "border-slate-200 bg-white hover:border-brand-300 hover:shadow-sm cursor-pointer"
          }`}
        >
          <input
            type      = "radio"
            name      = {name}
            value     = {option.value}
            checked   = {checked}
            disabled  = {disabled}
            onChange  = {() => onChange(option.value)}
            className = "sr-only"
          />
          <div className="flex items-center justify-between gap-2 min-h-[28px]">
            {option.icon ? (
              <span className={`inline-grid place-items-center w-8 h-8 rounded-lg transition-colors ${checked ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                <FontAwesomeIcon icon={option.icon} className="text-sm" />
              </span>
            ) : (
              <span className={`inline-grid place-items-center w-5 h-5 rounded-full border-2 ${checked ? "border-brand-500" : "border-slate-300"}`}>
                {checked && <span className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
              </span>
            )}
            {marked === option.value
              ? <MarkBadge kind={markKind} />
              : checked && option.icon && <FontAwesomeIcon icon="circle-check" className="text-brand-500" />}
          </div>
          <div className="text-sm font-semibold text-slate-900">{option.label}</div>
          {option.description && <div className="text-xs text-slate-500 leading-relaxed">{option.description}</div>}
        </label>
      )
    })}
  </div>
)

const withLabels = (options, labelKey) =>
  options.map(option => ({
    ...option,
    label:       t(`${labelKey}.${option.value}`),
    description: t(`${labelKey}.${option.value}_description`),
  }))

const ColumnMapper = ({ rows, columnCount, columns, onAssign, firstRow, onFirstRow }) => {
  const roleOf = index => COLUMN_ROLES.find(role => columns[role] === index)
  const indexes = Array.from({ length: columnCount }, (_, i) => i + 1)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {COLUMN_ROLES.map(role => (
          <span
            key       = {role}
            title     = {t(`roles.${role}_hint`)}
            className = {`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs ${
              columns[role] ? ROLE_TINT[role].head : "bg-white border-dashed border-slate-300 text-slate-400"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${columns[role] ? ROLE_TINT[role].dot : "bg-slate-300"}`} />
            {t(`roles.${role}`)}{role === "description" && " *"}
            <span className="opacity-70">
              {columns[role] ? `· ${t("column_short", { index: columns[role] })}` : `· ${t("not_assigned")}`}
            </span>
          </span>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50">
                <th className="w-14 px-2 py-2 border-b border-r border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-left">
                  {t("row")}
                </th>
                {indexes.map(index => {
                  const role       = roleOf(index)
                  const indexGuess = !role && !columns.code && looksLikeIndex(rows.map(row => row.cells[index - 1]))
                  return (
                    <th key={index} className={`min-w-[120px] p-1.5 border-b border-r border-slate-200 last:border-r-0 text-left font-normal ${role ? ROLE_TINT[role].cell : ""}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-1 mb-1">
                        {t("column_short", { index })}
                      </div>
                      <select
                        value     = {role || ""}
                        onChange  = {event => onAssign(index, event.target.value || null)}
                        className = {`w-full h-8 px-1.5 rounded-md border text-xs font-medium focus:border-brand-500 ${role ? ROLE_TINT[role].head : "bg-white border-slate-200 text-slate-500"}`}
                      >
                        <option value="">{t("ignore_column")}</option>
                        {COLUMN_ROLES.map(option => (
                          <option key={option} value={option}>{t(`roles.${option}`)}</option>
                        ))}
                      </select>
                      {indexGuess && (
                        <button
                          type      = "button"
                          onClick   = {() => onAssign(index, "code")}
                          title     = {t("looks_like_index")}
                          className = "mt-1 w-full inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded-md bg-purple-50 border border-dashed border-purple-300 text-[10px] font-semibold text-purple-700 hover:bg-purple-100"
                        >
                          <FontAwesomeIcon icon="wand-magic-sparkles" />
                          {t("use_as_index")}
                        </button>
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const skipped = firstRow && row.row < Number(firstRow)
                const start   = Number(firstRow) === row.row
                return (
                  <tr key={row.row} className={skipped ? "opacity-40" : ""}>
                    <td className={`px-2 py-1.5 border-b border-r border-slate-100 bg-slate-50/70 align-top ${start ? "border-t-2 border-t-brand-500" : ""}`}>
                      <button
                        type      = "button"
                        onClick   = {() => onFirstRow(row.row)}
                        title     = {t("set_first_row")}
                        className = {`inline-flex items-center gap-1 px-1.5 py-0.5 rounded tabular-nums ${start ? "bg-brand-500 text-white" : "text-slate-500 hover:bg-brand-50 hover:text-brand-700"}`}
                      >
                        {start && <FontAwesomeIcon icon="play" className="text-[8px]" />}
                        {row.row}
                      </button>
                    </td>
                    {indexes.map(index => {
                      const role  = roleOf(index)
                      const value = row.cells[index - 1] || ""
                      return (
                        <td
                          key       = {index}
                          title     = {value}
                          className = {`px-2 py-1.5 border-b border-r border-slate-100 last:border-r-0 max-w-[260px] truncate ${start ? "border-t-2 border-t-brand-500" : ""} ${role ? `${ROLE_TINT[role].cell} text-slate-800` : "text-slate-400"} ${["quantity", "unit_price", "total"].includes(role) ? "text-right tabular-nums" : ""}`}
                        >
                          {value}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
          <span><FontAwesomeIcon icon="arrow-pointer" className="mr-1" />{t("set_first_row_hint")}</span>
          {firstRow && <span className="font-medium text-slate-700 whitespace-nowrap">{t("start_row", { row: firstRow })}</span>}
        </div>
      </div>
    </div>
  )
}

const SummaryRow = ({ icon, label, value, onEdit }) => (
  <div className="flex items-start gap-3 py-3">
    <span className="inline-grid place-items-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 shrink-0">
      <FontAwesomeIcon icon={icon} className="text-sm" />
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value}</div>
    </div>
    <Button variant="ghost" size="sm" onClick={onEdit}>
      <FontAwesomeIcon icon="pen" />
      {t("edit")}
    </Button>
  </div>
)

const ConfigureImportModal = ({ quote, onClose, onConfigured }) => {
  const proposal        = quote?.import_proposal || {}
  const detailRows      = proposal.detail_rows || []
  const previewRows     = proposal.preview_rows || []
  const questions       = proposal.questions || []
  const recommendations = proposal.recommendations || {}
  const reasons         = recommendations.reasons || {}

  const steps = useMemo(
    () => ["columns", "hierarchy", "specs", ...(questions.length ? ["questions"] : []), "prices", "summary"],
    [questions.length]
  )

  const columnCount = useMemo(() => Math.max(
    0,
    ...previewRows.map(row => row.cells.reduce((last, cell, index) => (String(cell || "").trim() ? index + 1 : last), 0)),
    ...(proposal.column_samples || []).map(column => column.index)
  ), [quote?.id])

  const recommendedSpecs  = recommendations.specs || (detailRows.length ? "long_description" : "lines")
  const recommendedPrices = recommendations.prices || "none"

  const [step,       setStep]       = useState(0)
  const [reached,    setReached]    = useState(0)
  const [columns,    setColumns]    = useState({})
  const [hierarchy,  setHierarchy]  = useState("auto")
  const [firstRow,   setFirstRow]   = useState("")
  const [specs,      setSpecs]      = useState("long_description")
  const [prices,     setPrices]     = useState("none")
  const [answers,    setAnswers]    = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!quote) return
    setStep(0)
    setReached(0)
    setColumns(proposal.columns || {})
    setHierarchy(proposal.hierarchy || "auto")
    setFirstRow(proposal.first_row || "")
    setSpecs(recommendedSpecs)
    setPrices(recommendedPrices)
    setAnswers(questions.reduce((acc, question, index) => ({ ...acc, [index]: question.recommended || 0 }), {}))
    setSubmitting(false)
  }, [quote?.id])

  const hasPriceColumn = Boolean(columns.unit_price)

  useEffect(() => {
    if (!hasPriceColumn && prices !== "none") setPrices("none")
  }, [hasPriceColumn])

  const stepKey   = steps[step]
  const stepValid = stepKey !== "columns" || Boolean(columns.description)
  const isLast    = step === steps.length - 1

  const goTo = index => {
    setStep(index)
    setReached(current => Math.max(current, index))
  }

  const assignColumn = (index, role) => {
    const next = { ...columns }
    COLUMN_ROLES.forEach(key => { if (next[key] === index) next[key] = null })
    if (role) next[role] = index
    setColumns(next)
    if (next.code && !columns.code && hierarchy === "auto") setHierarchy("code")
    if (!next.code && hierarchy === "code") setHierarchy("auto")
  }

  const hierarchyMark = columns.code ? "code" : proposal.hierarchy

  const handleSubmit = () => {
    if (submitting || !columns.description) return
    setSubmitting(true)
    const body = {
      import: {
        columns:   COLUMN_ROLES.reduce((acc, role) => ({ ...acc, [role]: columns[role] || "" }), {}),
        hierarchy,
        first_row: firstRow,
        specs,
        prices,
        answers,
      },
    }
    postRequest({ url: quote.configure_import_url, body })
      .then(response => {
        if (!response?.success) {
          flashAlert((response?.errors || []).join(", ") || I18n.t("quotes.builder.footer.network_error"))
          setSubmitting(false)
          return
        }
        flashNotice(response.notice)
        onConfigured()
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
        setSubmitting(false)
      })
  }

  const chosenColumns = COLUMN_ROLES
    .filter(role => columns[role])
    .map(role => `${t(`roles.${role}`)} → ${t("column_short", { index: columns[role] })}`)
    .join(" · ")

  const panels = {
    columns: (
      <div className="space-y-4">
        <StepHeader title={t("columns_title")} hint={t("columns_hint")} />
        <Advice reason={reasons.columns} />
        <ColumnMapper
          rows        = {previewRows}
          columnCount = {columnCount}
          columns     = {columns}
          onAssign    = {assignColumn}
          firstRow    = {firstRow}
          onFirstRow  = {setFirstRow}
        />
      </div>
    ),

    hierarchy: (
      <div className="space-y-4">
        <StepHeader title={t("hierarchy_title")} hint={proposal.hierarchy_note && t("detected", { note: proposal.hierarchy_note })} />
        <Advice reason={reasons.hierarchy} />
        <OptionCards
          name     = "hierarchy"
          options  = {withLabels(HIERARCHY_OPTIONS, "hierarchy")}
          value        = {hierarchy}
          onChange     = {setHierarchy}
          marked       = {hierarchyMark}
          markKind     = {hierarchyMark === proposal.hierarchy ? "detected" : "recommended"}
          disabledWhen = {value => value === "code" && !columns.code}
        />
      </div>
    ),

    specs: (
      <div className="space-y-4">
        <StepHeader title={t("specs_title")} hint={t("specs_hint")} />
        <Advice reason={reasons.specs} />
        {detailRows.length > 0 ? (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("specs_found")}</div>
            <div className="flex flex-wrap gap-1.5">
              {detailRows.map(row => (
                <span key={row} className="px-2 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-700 italic">{row}</span>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{t("specs_none_found")}</p>
        )}
        <OptionCards
          name     = "specs"
          options  = {withLabels(SPECS_OPTIONS, "specs")}
          value    = {specs}
          onChange = {setSpecs}
          marked   = {recommendedSpecs}
        />
      </div>
    ),

    questions: (
      <div className="space-y-5">
        <StepHeader title={t("questions_title")} hint={t("questions_hint")} />
        {questions.map((question, index) => (
          <div key={index} className="space-y-2.5">
            <div className="flex items-start gap-2.5">
              <span className="inline-grid place-items-center w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-semibold shrink-0">{index + 1}</span>
              <p className="text-sm font-medium text-slate-900 pt-0.5">{question.question}</p>
            </div>
            <div className="pl-8">
              <OptionCards
                name     = {`question-${index}`}
                options  = {question.answers.map((answer, answerIndex) => ({ value: answerIndex, label: answer }))}
                value    = {answers[index]}
                onChange = {value => setAnswers({ ...answers, [index]: value })}
                marked   = {question.recommended}
                columns  = {question.answers.length > 2 ? 3 : 2}
              />
            </div>
          </div>
        ))}
      </div>
    ),

    prices: (
      <div className="space-y-4">
        <StepHeader title={t("prices_title")} hint={hasPriceColumn ? t("prices_hint") : t("prices_no_column")} />
        <Advice reason={reasons.prices} />
        <OptionCards
          name         = "prices"
          options      = {withLabels(PRICES_OPTIONS, "prices")}
          value        = {prices}
          onChange     = {setPrices}
          marked       = {hasPriceColumn ? recommendedPrices : null}
          disabledWhen = {value => value !== "none" && !hasPriceColumn}
        />
        {prices !== "none" && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-brand-50/70 border border-brand-100 text-sm text-slate-700">
            <FontAwesomeIcon icon="wand-magic-sparkles" className="text-brand-500 mt-0.5" />
            {t("prices_cost_type_note")}
          </div>
        )}
      </div>
    ),

    summary: (
      <div className="space-y-4">
        <StepHeader title={t("summary_title")} hint={t("summary_hint")} />
        <div className="px-4 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          <SummaryRow icon="table-columns" label={t("steps.columns")} onEdit={() => goTo(steps.indexOf("columns"))}
                      value={<>{chosenColumns || "—"}{firstRow && <span className="block text-xs text-slate-500 mt-0.5">{t("start_row", { row: firstRow })}</span>}</>} />
          <SummaryRow icon="sitemap" label={t("steps.hierarchy")} value={t(`hierarchy.${hierarchy}`)} onEdit={() => goTo(steps.indexOf("hierarchy"))} />
          <SummaryRow icon="tags"    label={t("steps.specs")}     value={t(`specs.${specs}`)}         onEdit={() => goTo(steps.indexOf("specs"))} />
          {questions.length > 0 && (
            <SummaryRow
              icon   = "circle-question"
              label  = {t("steps.questions")}
              onEdit = {() => goTo(steps.indexOf("questions"))}
              value  = {questions.map((question, index) => (
                <span key={index} className="block">
                  <span className="text-slate-500">{question.question}</span> → {question.answers[answers[index]]}
                </span>
              ))}
            />
          )}
          <SummaryRow icon="euro-sign" label={t("steps.prices")} value={t(`prices.${prices}`)} onEdit={() => goTo(steps.indexOf("prices"))} />
        </div>
      </div>
    ),
  }

  return (
    <Modal
      isOpen  = {quote !== null}
      onClose = {onClose}
      size    = "xl"
      eyebrow = {quote?.title}
      title   = {t("title")}
      footer  = {
        <>
          <span className="text-xs text-slate-500">
            {stepValid ? t("step_counter", { current: step + 1, total: steps.length }) : t("description_required")}
          </span>
          <div className="flex items-center gap-2">
            {step === 0
              ? <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
              : (
                <Button variant="ghost" onClick={() => setStep(step - 1)}>
                  <FontAwesomeIcon icon="arrow-left" />
                  {t("back")}
                </Button>
              )}
            {isLast ? (
              <Button variant="primary" onClick={handleSubmit} disabled={submitting || !columns.description}>
                <FontAwesomeIcon icon={submitting ? "spinner" : "file-import"} spin={submitting} />
                {t("submit")}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => goTo(step + 1)} disabled={!stepValid}>
                {t("next")}
                <FontAwesomeIcon icon="arrow-right" />
              </Button>
            )}
          </div>
        </>
      }
    >
      <Stepper steps={steps} current={step} reached={reached} onJump={index => (stepValid || index < step) && goTo(index)} />
      <div key={stepKey} className="animate-[qiFadeIn_250ms_ease-out]">
        {panels[stepKey]}
      </div>
      <style>{"@keyframes qiFadeIn { from { opacity: 0; transform: translateX(8px) } to { opacity: 1; transform: none } }"}</style>
    </Modal>
  )
}

export default ConfigureImportModal
